export interface AudioReactiveBands {
  bass: number;
  mid: number;
  treble: number;
  beat: number;
  energy: number;
}

const ZERO_BANDS: AudioReactiveBands = {
  bass: 0,
  mid: 0,
  treble: 0,
  beat: 0,
  energy: 0,
};

type BandListener = (bands: AudioReactiveBands) => void;

let ctx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let source: MediaElementAudioSourceNode | null = null;
let connectedEl: HTMLAudioElement | null = null;
let freqData: Uint8Array | null = null;
let timeData: Uint8Array | null = null;
let rafId = 0;
let consumerCount = 0;
let loopRunning = false;
let resolveAudio: () => HTMLAudioElement | null = () => null;

let prevBass = 0;
let beatEnv = 0;
let lastBeatAt = 0;
let smoothBass = 0;
let smoothMid = 0;
let smoothTreble = 0;

/** 跨域直链无法 Web Audio 时的烛光式模拟律动（封面/歌词共用） */
let simClock = 0;
let simLastNow = 0;
let simNextGust = 0.5;
let simGustEnv = 0;
let simBass = 0.34;
let simMid = 0.26;
let simTreble = 0.18;
let simPhaseA = Math.random() * Math.PI * 2;
let simPhaseB = Math.random() * Math.PI * 2;
let simPhaseC = Math.random() * Math.PI * 2;
let simPhaseD = Math.random() * Math.PI * 2;
let simWarp = Math.random() * Math.PI * 2;

const listeners = new Set<BandListener>();

function broadcast(bands: AudioReactiveBands) {
  listeners.forEach((listener) => listener(bands));
}

function resetSimulatedRhythm() {
  simClock = 0;
  simLastNow = 0;
  simNextGust = 0.35 + Math.random() * 0.9;
  simGustEnv = 0;
  simBass = 0.34;
  simMid = 0.26;
  simTreble = 0.18;
  simPhaseA = Math.random() * Math.PI * 2;
  simPhaseB = Math.random() * Math.PI * 2;
  simPhaseC = Math.random() * Math.PI * 2;
  simPhaseD = Math.random() * Math.PI * 2;
  simWarp = Math.random() * Math.PI * 2;
}

/**
 * 烛光式起伏：多层慢速正弦 + 不规则微颤 + 偶发轻柔「一阵风」。
 * 刻意避开机械节拍；强度保证封面能动，又不刺眼。
 */
function sampleSimulatedBands(): AudioReactiveBands {
  const now = performance.now() / 1000;
  if (!simLastNow) simLastNow = now;
  const dt = Math.min(0.05, Math.max(0, now - simLastNow));
  simLastNow = now;
  simClock += dt;

  // 相位缓慢漂移，避免周期性机械循环
  simWarp += dt * (0.11 + Math.sin(simClock * 0.07) * 0.04);
  simPhaseA += dt * (1.35 + Math.sin(simWarp) * 0.25);
  simPhaseB += dt * (2.4 + Math.cos(simWarp * 1.3) * 0.45);
  simPhaseC += dt * (3.9 + Math.sin(simWarp * 0.6) * 0.55);
  simPhaseD += dt * (0.55 + Math.cos(simWarp * 0.9) * 0.12);

  const flicker =
    0.52
    + Math.sin(simPhaseA) * 0.16
    + Math.sin(simPhaseB) * 0.11
    + Math.sin(simPhaseC) * 0.07
    + Math.sin(simPhaseD) * 0.14;
  // 高频微颤：像烛芯抖动
  const shimmer = Math.sin(simClock * 11.3 + simWarp * 2.1) * 0.035
    + Math.sin(simClock * 17.7 + simPhaseB) * 0.02;

  if (simClock >= simNextGust) {
    // 间隔随机：0.45s～2.4s，强度随机偏柔
    simGustEnv = Math.max(simGustEnv, 0.28 + Math.random() * 0.38);
    simNextGust = simClock + 0.45 + Math.random() * 1.95;
  }
  simGustEnv *= Math.exp(-dt / 0.42);

  const bedBass = 0.32 + flicker * 0.2 + shimmer * 0.6;
  const bedMid = 0.24 + flicker * 0.14 + shimmer * 0.35;
  const bedTreble = 0.16 + flicker * 0.1 + Math.abs(shimmer) * 0.5;

  const follow = 1 - Math.exp(-dt * 5.5);
  simBass += (bedBass + simGustEnv * 0.42 - simBass) * follow;
  simMid += (bedMid + simGustEnv * 0.26 - simMid) * follow;
  simTreble += (bedTreble + simGustEnv * 0.16 - simTreble) * follow;

  const bass = Math.min(0.82, Math.max(0.28, simBass));
  const mid = Math.min(0.62, Math.max(0.2, simMid));
  const treble = Math.min(0.48, Math.max(0.14, simTreble));
  // beat 用柔和包络，不打节拍器感
  const beat = Math.min(0.72, simGustEnv * 0.85 + Math.max(0, flicker - 0.55) * 0.55);
  const energy = Math.min(0.68, (bass * 1.15 + mid + treble) / 3.15);

  return { bass, mid, treble, beat, energy };
}

function decayBands() {
  smoothBass *= 0.82;
  smoothMid *= 0.82;
  smoothTreble *= 0.82;
  beatEnv *= 0.88;
  simGustEnv *= 0.88;
  simBass *= 0.9;
  simMid *= 0.9;
  simTreble *= 0.9;
  broadcast({
    bass: smoothBass,
    mid: smoothMid,
    treble: smoothTreble,
    beat: beatEnv,
    energy: (smoothBass + smoothMid + smoothTreble) / 3,
  });
}

function ensureContext() {
  if (ctx) return ctx;
  const Ctx = window.AudioContext
    || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  ctx = new Ctx();
  return ctx;
}

/** blob / 同源 URL 才允许接入 Web Audio；跨域 CDN（如阿里云直链）接入后会无声 */
export function canAnalyzeAudioUrl(rawUrl: string | null | undefined): boolean {
  const src = (rawUrl || '').trim();
  if (!src || src === window.location.href) return false;
  if (src.startsWith('blob:') || src.startsWith('data:')) return true;
  try {
    return new URL(src, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function canAnalyzeAudioElement(audio: HTMLAudioElement | null | undefined): boolean {
  if (!audio) return false;
  return canAnalyzeAudioUrl(audio.currentSrc || audio.src);
}

export function getWebAudioTappedElement(): HTMLAudioElement | null {
  return connectedEl;
}

/** 放弃当前 tap 状态（需配合 AudioController 重建 audio 元素） */
export function resetWebAudioTapState() {
  try {
    source?.disconnect();
  } catch {
    /* ignore */
  }
  try {
    analyser?.disconnect();
  } catch {
    /* ignore */
  }
  connectedEl = null;
  source = null;
  analyser = null;
  freqData = null;
  timeData = null;
}

/** 关闭分析用 AudioContext，便于切后台后回到 HTMLAudio 原生出声 */
export async function closeCoverParticleAudioContext() {
  resetWebAudioTapState();
  const context = ctx;
  ctx = null;
  if (!context) return;
  try {
    await context.close();
  } catch {
    /* ignore */
  }
}

/** 是否已把指定 audio 接到 Web Audio（出声依赖 AudioContext） */
export function isWebAudioTappedElement(audio: HTMLAudioElement | null | undefined): boolean {
  return Boolean(audio && connectedEl === audio && source);
}

/**
 * createMediaElementSource 对同一 audio 只能调用一次，且会切断原生输出。
 * 跨域 CDN / 无 CORS 的音频绝不能接入，否则「进度在走但无声」。
 */
function ensureGraph(audio: HTMLAudioElement): boolean {
  const context = ensureContext();
  if (!context) return false;

  if (!canAnalyzeAudioElement(audio)) {
    return false;
  }

  if (connectedEl === audio && analyser && source) {
    if (context.state === 'suspended') {
      context.resume().catch(() => {});
    }
    return context.state === 'running';
  }
  if (connectedEl && connectedEl !== audio) {
    return false;
  }

  // 未 running 时绝不 createMediaElementSource，保留 HTMLAudioElement 原生出声
  // 页面在后台时也不接入：iOS 会挂起 AudioContext，导致「进度在走但无声 / 回前台降调」
  if (typeof document !== 'undefined' && document.hidden) {
    return false;
  }
  if (context.state !== 'running') {
    context.resume().catch(() => {});
    return false;
  }

  try {
    source = context.createMediaElementSource(audio);
    analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.48;
    source.connect(analyser);
    analyser.connect(context.destination);
    connectedEl = audio;
    freqData = new Uint8Array(analyser.frequencyBinCount);
    timeData = new Uint8Array(analyser.fftSize);
    return true;
  } catch (err) {
    console.warn('[CoverParticleAudioBridge] 音频分析接入失败:', err);
    return false;
  }
}

function sample() {
  const audio = resolveAudio();
  if (!audio || audio.paused || audio.ended) {
    decayBands();
    return;
  }

  // 无 CORS 的跨域直链：不能接 Web Audio，改发自然模拟律动（封面/歌词同一路）
  if (!canAnalyzeAudioElement(audio)) {
    broadcast(sampleSimulatedBands());
    return;
  }

  if (!ensureGraph(audio) || !analyser || !freqData || !timeData) {
    // Context 尚未 resume 等短暂窗口，先用模拟顶上，避免粒子静止
    broadcast(sampleSimulatedBands());
    return;
  }

  if (ctx?.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  analyser.getByteFrequencyData(freqData);
  analyser.getByteTimeDomainData(timeData);

  const len = freqData.length;
  let bassSum = 0;
  let midSum = 0;
  let trebleSum = 0;
  const bassEnd = Math.min(12, len);
  const midEnd = Math.min(48, len);

  for (let i = 0; i < bassEnd; i += 1) bassSum += freqData[i];
  for (let i = bassEnd; i < midEnd; i += 1) midSum += freqData[i];
  for (let i = midEnd; i < len; i += 1) trebleSum += freqData[i];

  let bass = bassSum / (bassEnd * 255);
  let mid = midSum / ((midEnd - bassEnd) * 255);
  let treble = trebleSum / ((len - midEnd) * 255);

  // 时域冲击：对 kick 更敏感
  let peak = 0;
  for (let i = 0; i < timeData.length; i += 1) {
    const v = Math.abs(timeData[i] - 128) / 128;
    if (v > peak) peak = v;
  }
  bass = Math.max(bass, peak * 0.85);

  const energy = (bass * 1.15 + mid + treble) / 3.15;
  smoothBass = smoothBass * 0.58 + bass * 0.42;
  smoothMid = smoothMid * 0.62 + mid * 0.38;
  smoothTreble = smoothTreble * 0.62 + treble * 0.38;

  const bassDelta = bass - prevBass;
  prevBass = prevBass * 0.45 + bass * 0.55;
  const now = performance.now();
  let beat = 0;
  if ((bass > 0.18 && bassDelta > 0.028) || peak > 0.22) {
    if (now - lastBeatAt > 140) {
      beat = 1;
      lastBeatAt = now;
    }
  }
  beatEnv = Math.max(beat, beatEnv * 0.82);

  broadcast({
    bass: Math.min(1.2, smoothBass * 1.35),
    mid: Math.min(1.2, smoothMid * 1.2),
    treble: Math.min(1.2, smoothTreble * 1.15),
    beat: beatEnv,
    energy: Math.min(1.2, energy * 1.25),
  });
}

function tick() {
  if (!loopRunning) {
    rafId = 0;
    return;
  }
  rafId = requestAnimationFrame(tick);
  sample();
}

function startLoop() {
  loopRunning = true;
  if (!rafId) rafId = requestAnimationFrame(tick);
}

function stopLoop() {
  loopRunning = false;
  if (rafId) cancelAnimationFrame(rafId);
  rafId = 0;
  prevBass = 0;
  beatEnv = 0;
  smoothBass = 0;
  smoothMid = 0;
  smoothTreble = 0;
  resetSimulatedRhythm();
  broadcast(ZERO_BANDS);
}

/** 由播放器告知当前正在使用的 audio 元素 */
export function setCoverParticleAudioProvider(provider: () => HTMLAudioElement | null) {
  resolveAudio = provider;
}

export function subscribeCoverParticleAudio(listener: BandListener): () => void {
  listeners.add(listener);
  consumerCount += 1;
  startLoop();
  return () => {
    listeners.delete(listener);
    consumerCount -= 1;
    if (consumerCount <= 0) stopLoop();
  };
}

export async function resumeCoverParticleAudioContext() {
  const context = ensureContext();
  if (!context) return;
  if (context.state === 'suspended') {
    try {
      await context.resume();
    } catch {
      /* ignore */
    }
  }
}

/** 在用户点击/播放手势中同步唤醒 AudioContext，避免粒子封面接入后无声 */
export function prepareCoverParticleAudioContext() {
  const context = ensureContext();
  if (!context) return;
  if (context.state === 'suspended') {
    context.resume().catch(() => {});
  }
}

export function getCoverParticleAudioDebugState() {
  return {
    connected: Boolean(connectedEl && analyser),
    contextState: ctx?.state ?? 'none',
    consumerCount,
    listenerCount: listeners.size,
    tappedElement: connectedEl,
    canAnalyzeTapped: canAnalyzeAudioElement(connectedEl),
  };
}
